import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  portalComplianceUpload, portalComplianceAddItem, portalComplianceDeleteFile, portalComplianceFileUrl, portalComplianceRequestWaiver,
  apiErrText, complianceReturnLine,
  COMPLIANCE_STATUS, COMPLIANCE_CATEGORY, COMPLIANCE_CADENCE, CONTRACT_FORM_LABEL, COMPLIANCE_POLICY_ONLY_LABEL,
  type PortalCompliance, type ComplianceItemView, type EffectiveComplianceStatus,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 履约档案 / the contractor's compliance file (Layer 1 step 4A · Eric 9/18).
 *
 * Eric 9/18 (compliance file): this is a PHONE surface first. 江卫春's people stand on a site with a
 * 营业执照 in one hand and a phone in the other, so every slot offers 拍照上传 next to 选择文件, and the
 * 待办 card at the top answers "what do they want from me today" without scrolling a 21-row checklist.
 *
 * Money-free by construction: the panel renders only what GET …/compliance returns, and that payload
 * carries no prices (server lib/compliance.ts guarantees it). Nothing here fetches bids or totals.
 */

/** Statuses that mean "the contractor still has work to do" — same set the server's laborTodo uses. */
const TODO_STATUSES: EffectiveComplianceStatus[] = ['missing', 'returned', 'expiring', 'expired', 'lapsed'];
/** Mirrors the server caps (portal.ts COMPLIANCE_MAX_FILES / COMPLIANCE_FILE_LIMIT_MB) so a phone on a
 *  site 4G link is told NOW instead of after uploading 200MB and being refused. */
const MAX_FILES = 10;
const MAX_FILE_BYTES = 30 * 1024 * 1024;
// .ofd — the 国标 e-invoice format (改改 9/21 fb_8ef675e1: an H2 发票 often arrives as one)
const ACCEPT_ANY = '.pdf,.ofd,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif,.xlsx,.xls,.docx,.doc';

function fmtBytes(n: number | null): string {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
/** Dates arrive as 'YYYY-MM-DD' or a full ISO stamp; the portal only ever shows the calendar day. */
function fmtDay(iso: string | null): string { return iso ? String(iso).slice(0, 10) : ''; }
/** 到期 for a to-do row: an expiry beats a due date — it is the one that extinguishes something. */
function dueDay(it: ComplianceItemView): string { return fmtDay(it.expires_at) || fmtDay(it.due_at); }

export default function PortalCompliancePanel({ eid, data }: { eid: string; data: PortalCompliance }) {
  const qc = useQueryClient();
  const [addCode, setAddCode] = useState('');
  const [adding, setAdding] = useState(false);
  // Categories start collapsed EXCEPT the ones holding outstanding work — on a phone, 8 open sections
  // is a wall of text, and what matters is the few slots the 待办 card just pointed at.
  const [openCats, setOpenCats] = useState<Record<string, boolean> | null>(null);

  const items = data.items;
  const todo = useMemo(
    () => items.filter((i) => i.required === 1 && TODO_STATUSES.includes(i.effective_status)),
    [items],
  );
  const byCategory = useMemo(() => {
    const m = new Map<string, ComplianceItemView[]>();
    for (const i of items) { const arr = m.get(i.category) ?? []; arr.push(i); m.set(i.category, arr); }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.sort_order - b.sort_order || a.item_code.localeCompare(b.item_code) || (a.period_key ?? '').localeCompare(b.period_key ?? ''));
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);
  const defaultOpen = useMemo(() => {
    const o: Record<string, boolean> = {};
    for (const [cat, arr] of byCategory) o[cat] = arr.some((i) => TODO_STATUSES.includes(i.effective_status));
    return o;
  }, [byCategory]);
  const cats = openCats ?? defaultOpen;
  const toggleCat = (cat: string) => setOpenCats({ ...cats, [cat]: !cats[cat] });

  /** Refresh after any write. setQueryData first so the new status paints before the refetch lands. */
  function applyItem(item: ComplianceItemView) {
    qc.setQueryData<PortalCompliance>(['portal-compliance', eid], (prev) =>
      prev ? { ...prev, items: prev.items.some((i) => i.id === item.id) ? prev.items.map((i) => (i.id === item.id ? item : i)) : [...prev.items, item] } : prev);
    void qc.invalidateQueries({ queryKey: ['portal-compliance', eid] });
    void qc.invalidateQueries({ queryKey: ['portal-todo-count'] });
  }

  /** ＋新增记录 — open a per-event slot (材料报批, 隐蔽验收, 发票, 补充协议…), then upload into it. */
  async function addRecord() {
    if (!addCode) return;
    setAdding(true);
    try {
      const { item } = await portalComplianceAddItem(eid, addCode);
      applyItem(item);
      setOpenCats({ ...cats, [item.category]: true });
      setAddCode('');
      toast(`已新增「${item.label_zh}」，请上传资料。/ Record added — upload the documents.`, { kind: 'success' });
      // give React the frame it needs to render the new row before scrolling to it
      window.setTimeout(() => document.getElementById(`ci-${item.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
    } catch (e) {
      toast(apiErrText(e), { kind: 'error' });
    } finally { setAdding(false); }
  }

  /** 待办 jump: expand the slot's category, then bring it under the thumb. */
  function jumpTo(it: ComplianceItemView) {
    setOpenCats({ ...cats, [it.category]: true });
    window.setTimeout(() => document.getElementById(`ci-${it.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  }

  const ec = data.executed_contract;
  return (
    <div>
      {/* ── 待办 / what WTL is waiting for ─────────────────────────────────────────────────────────── */}
      <div className="card" style={{ margin: '10px 0', borderLeft: `3px solid ${todo.length ? 'var(--bad)' : 'var(--good)'}` }}>
        {todo.length === 0 ? (
          <div className="small" style={{ color: 'var(--good)' }}>
            ✓ 必备资料已齐全，暂无待办。/ All required documents are in — nothing outstanding.
          </div>
        ) : (
          <>
            <div className="small"><strong>还缺 {todo.length} 项必备资料 / {todo.length} required document(s) still missing</strong></div>
            <div className="muted small" style={{ margin: '2px 0 8px' }}>
              请逐项上传；预算审核确认后该项才算齐全。/ Upload each one — an item counts as complete only after WTL confirms it.
            </div>
            {/* 江卫春 9/20 (fb_93556959): 「这19项都是必填项吗？有个劳务是没有这些资质」— they are all contract
                obligations, and the relief (免除) was invisible from here. Say so, and point at the door. */}
            <div className="small" style={{ margin: '0 0 8px', padding: '6px 8px', background: 'var(--paper-2, rgba(0,0,0,0.04))', borderRadius: 6 }}>
              以上均为合同约定的必备资料。如贵司确实没有某项资质或该项不适用，请在该项下点「无此资质 · 申请免除」并说明原因，由预算/总经理确认后免除，无需上传。
              <span className="muted"> / All of these are contractual obligations. If your company genuinely does not hold one, or it does not apply, use 「申请免除 / Request waiver」 on that item with a reason — WTL confirms and the item is waived, nothing to upload.</span>
            </div>
            {todo.map((it) => {
              const st = COMPLIANCE_STATUS[it.effective_status];
              const day = dueDay(it);
              return (
                <div key={it.id} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '5px 0', borderTop: '1px solid var(--rule-hair)' }}>
                  <span className={`pill small ${st.pill}`}>{st.label}</span>
                  <span style={{ flex: 1, minWidth: 140, wordBreak: 'break-word' }}>
                    <strong>{it.label_zh}</strong>
                    {it.period_key && <span className="muted"> · {it.period_key}</span>}
                    <span className="muted small"> / {it.label_en}</span>
                    {day && <span className="muted small"> · 到期 / due {day}</span>}
                    {it.effective_status === 'returned' && (
                      <div className="small" style={{ color: 'var(--bad)', marginTop: 2 }}>{complianceReturnLine(it.review_note_md)}</div>
                    )}
                    {it.effective_status === 'lapsed' && (
                      <div className="small" style={{ color: 'var(--bad)', marginTop: 2 }}>
                        已超过合同约定的期限，该项权利可能已失效 — 仍请上传并立即联系 WTL。/ past the contractual deadline — upload anyway and contact WTL at once.
                      </div>
                    )}
                  </span>
                  <button className="small" onClick={() => jumpTo(it)}>⬆ 上传 / Upload</button>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ── 已签署合同扫描件 (Eric 9/18: the contractor gets its own chopped paper back) ───────────── */}
      <div className="card" style={{ marginBottom: 10 }}>
        <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline' }}>
          <strong className="small">已签署合同扫描件 / Executed contract scan</strong>
          {data.contract_form && <span className="pill small pill-muted">{CONTRACT_FORM_LABEL[data.contract_form]}</span>}
        </div>
        {ec ? (
          <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline', marginTop: 4 }}>
            <a href={portalComplianceFileUrl(eid, ec.file_id)} style={{ flex: 1, minWidth: 120, wordBreak: 'break-all' }}>⬇ {ec.file_name}</a>
            {ec.signed_on && <span className="muted small">签署日期 / signed {fmtDay(ec.signed_on)}</span>}
          </div>
        ) : (
          <div className="muted small" style={{ marginTop: 4 }}>
            盖章合同扫描件尚未上传 — WTL 记录签署后这里会出现下载链接。/ Not uploaded yet — the download appears once WTL records the signing.
          </div>
        )}
      </div>

      {/* ── 我的档案 by category A–H ─────────────────────────────────────────────────────────────── */}
      <div className="row-wrap" style={{ gap: 8, alignItems: 'baseline', marginTop: 14 }}>
        <h3 style={{ margin: 0 }}>我的档案 / My compliance file</h3>
        <span className="muted small">
          必备 {data.counts.required_done}/{data.counts.required_total} 已确认 · 待审 {data.counts.awaiting_review}
          {' '}/ {data.counts.required_done} of {data.counts.required_total} required confirmed · {data.counts.awaiting_review} in review
        </span>
      </div>
      <div className="muted small" style={{ margin: '2px 0 8px' }}>
        目前提醒只在本页和顶部「待办」徽标显示，请定期查看。/ For now reminders live on this page and the 待办 badge — check back regularly.
      </div>

      {byCategory.length === 0 && <div className="card muted small">档案清单尚未生成，请稍后刷新。/ The checklist has not been built yet — refresh in a moment.</div>}

      {byCategory.map(([cat, arr]) => {
        const outstanding = arr.filter((i) => TODO_STATUSES.includes(i.effective_status)).length;
        const isOpen = !!cats[cat];
        return (
          <div key={cat} className="card" style={{ marginBottom: 8 }}>
            <button
              onClick={() => toggleCat(cat)}
              aria-expanded={isOpen}
              style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}
            >
              <span style={{ flex: 1, minWidth: 140, wordBreak: 'break-word' }}><strong>{COMPLIANCE_CATEGORY[cat] ?? cat}</strong></span>
              {outstanding > 0 && <span className="pill small pill-bad">待办 {outstanding}</span>}
              <span className="muted small">{arr.length} 项</span>
              <span className="muted small">{isOpen ? '▾' : '▸'}</span>
            </button>
            {isOpen && arr.map((it) => (
              <ComplianceItemRow key={it.id} eid={eid} item={it} onItem={applyItem} />
            ))}
          </div>
        );
      })}

      {/* ── ＋新增记录 (per-event types) ─────────────────────────────────────────────────────────── */}
      {data.event_types.length > 0 && (
        <div className="card" style={{ marginBottom: 10 }}>
          <div className="small"><strong>＋ 新增记录 / Add a record</strong></div>
          <div className="muted small" style={{ margin: '2px 0 6px' }}>
            材料报批、隐蔽工程验收、发票、补充协议等按事件发生时逐次新增。/ Per-event records — add one each time it happens.
          </div>
          <div className="row-wrap" style={{ gap: 6, alignItems: 'center' }}>
            <select value={addCode} onChange={(e) => setAddCode(e.target.value)} style={{ flex: 1, minWidth: 160 }} aria-label="记录类型 / record type">
              <option value="">选择记录类型 / choose a record type…</option>
              {data.event_types.map((t) => (
                <option key={t.code} value={t.code}>{t.code} {t.label_zh} / {t.label_en}</option>
              ))}
            </select>
            <button className="btn-primary" disabled={!addCode || adding} onClick={addRecord}>
              {adding ? '新增中… / adding…' : '＋ 新增记录 / Add a record'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** One checklist slot: what it is, what state it is in, the files on it, and its upload slot. */
function ComplianceItemRow({ eid, item, onItem }: { eid: string; item: ComplianceItemView; onItem: (i: ComplianceItemView) => void }) {
  const st = COMPLIANCE_STATUS[item.effective_status];
  const day = dueDay(item);
  const [busyFile, setBusyFile] = useState<string | null>(null);

  async function removeFile(fileId: string, name: string) {
    if (!window.confirm(`删除「${name}」？/ Remove 「${name}」?`)) return;
    setBusyFile(fileId);
    try {
      const { item: updated } = await portalComplianceDeleteFile(eid, fileId);
      onItem(updated);
      toast('已删除 / removed', { kind: 'success' });
    } catch (e) {
      toast(apiErrText(e), { kind: 'error' });
    } finally { setBusyFile(null); }
  }

  return (
    <div id={`ci-${item.id}`} style={{ padding: '8px 0', borderTop: '1px solid var(--rule)' }}>
      <div className="row-wrap" style={{ gap: 6, alignItems: 'baseline' }}>
        <span style={{ flex: 1, minWidth: 140, wordBreak: 'break-word' }}>
          <strong>{item.item_code} {item.label_zh}</strong>
          {item.period_key && <span className="pill small pill-muted" style={{ marginLeft: 6 }}>{item.period_key}</span>}
          <div className="muted small">{item.label_en}</div>
        </span>
        <span className={`pill small ${st.pill}`}>{st.label}</span>
      </div>
      <div className="row-wrap" style={{ gap: 6, alignItems: 'baseline', marginTop: 2 }}>
        <span className="pill small pill-muted">{COMPLIANCE_CADENCE[item.cadence]}</span>
        {item.required === 0 && <span className="pill small pill-muted">选填 / Optional</span>}
        {item.policy_only === 1 && <span className="pill small pill-muted" title={COMPLIANCE_POLICY_ONLY_LABEL}>{COMPLIANCE_POLICY_ONLY_LABEL}</span>}
        {item.wtl_countersign === 1 && <span className="pill small pill-muted">需 WTL 会签 / WTL countersigns</span>}
        {day && <span className="muted small">到期 / due {day}</span>}
        {item.clause_ref && <span className="muted small">条款 / clause {item.clause_ref}</span>}
      </div>
      {(item.evidence_hint_zh || item.evidence_hint_en) && (
        <div className="muted small" style={{ marginTop: 2 }}>
          需要 / provide：{item.evidence_hint_zh}{item.evidence_hint_en ? ` / ${item.evidence_hint_en}` : ''}
        </div>
      )}
      {item.effective_status === 'returned' && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{complianceReturnLine(item.review_note_md)}</div>
      )}
      {item.status === 'waived' && (
        <div className="muted small" style={{ marginTop: 4 }}>
          已免除 / waived{item.waive_reason_md ? `：${item.waive_reason_md}` : ''} — 本项无需上传。/ nothing to upload for this item.
        </div>
      )}
      {item.note_md && <div className="muted small" style={{ marginTop: 2 }}>备注 / note：{item.note_md}</div>}
      {item.waiver_requested_at && (item.status === 'missing' || item.status === 'returned') && (
        <div className="small" style={{ marginTop: 4, color: 'var(--accent)' }}>
          已申请免除（{fmtDay(item.waiver_requested_at)}），等待 WTL 确认：{item.waiver_request_md}
          <span className="muted"> / waiver requested — awaiting WTL</span>
        </div>
      )}

      {item.files.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {item.files.map((f) => (
            <div key={f.id} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
              <a href={portalComplianceFileUrl(eid, f.file_id)} style={{ flex: 1, minWidth: 120, wordBreak: 'break-all' }}>⬇ {f.file_name}</a>
              <span className="muted small">{fmtBytes(f.size_bytes)}</span>
              {f.uploaded_side === 'wtl' && <span className="pill small pill-muted">WTL 代上传 / by WTL</span>}
              {item.status !== 'confirmed' && f.uploaded_side === 'labor' && (
                <button className="small" disabled={busyFile === f.file_id} onClick={() => removeFile(f.file_id, f.file_name)}>
                  {busyFile === f.file_id ? '删除中…' : '✕ 删除 / Remove'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <UploadSlot eid={eid} item={item} onItem={onItem} />
      {item.required === 1 && (item.status === 'missing' || item.status === 'returned') && (
        <WaiverRequestSlot eid={eid} item={item} onItem={onItem} />
      )}
    </div>
  );
}

/**
 * 「无此资质 · 申请免除」(江卫春 9/20): a required slot the contractor genuinely cannot fill. Sends a reason
 * to WTL; the slot stays 缺失 until gm/预算 press 免除 on their side. Collapsed to one link so the row
 * still reads "upload this" first — the waiver is the exception, not the default path.
 */
function WaiverRequestSlot({ eid, item, onItem }: { eid: string; item: ComplianceItemView; onItem: (i: ComplianceItemView) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const already = !!item.waiver_requested_at;

  async function send() {
    if (!reason.trim()) { setErr('请说明为何无法提供该资料。/ Say why this document cannot be provided.'); return; }
    setBusy(true); setErr(null);
    try {
      const { item: updated } = await portalComplianceRequestWaiver(eid, item.id, reason.trim());
      onItem(updated);
      setOpen(false); setReason('');
      toast('已提交免除申请，WTL 确认后本项将免除。/ Waiver requested — WTL will confirm.', { kind: 'success' });
    } catch (e) { setErr(apiErrText(e)); } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <div style={{ marginTop: 6 }}>
        <button className="small" disabled={busy} onClick={() => { setOpen(true); setReason(item.waiver_request_md ?? ''); }}>
          {already ? '✎ 修改免除申请 / Edit waiver request' : '无此资质 · 申请免除 / Not applicable · request waiver'}
        </button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6, padding: '8px', border: '1px dashed var(--rule)', borderRadius: 6 }}>
      <div className="small" style={{ marginBottom: 4 }}>
        <strong>申请免除 / Request waiver：{item.item_code} {item.label_zh}</strong>
        <div className="muted">说明贵司为何没有该资料（如：仅承接劳务分包，无施工资质）。预算/总经理确认后本项免除。/ Explain why your company does not hold this. WTL confirms, then the item is waived.</div>
      </div>
      <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} style={{ width: '100%', boxSizing: 'border-box' }}
        placeholder="原因（必填）/ reason (required)" disabled={busy} />
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{err}</div>}
      <div className="row-wrap" style={{ gap: 6, marginTop: 6 }}>
        <button className="btn-primary small" disabled={busy || !reason.trim()} onClick={send}>{busy ? '提交中…' : '✓ 提交申请 / Submit'}</button>
        <button className="small" disabled={busy} onClick={() => { setOpen(false); setErr(null); }}>取消 / Cancel</button>
      </div>
    </div>
  );
}

/**
 * Pick-then-confirm upload (fb_ac916632, 江卫春): choosing a file never uploads it. The chosen names are
 * listed, a wrong pick can be cleared, and 「✓ 确认上传」 is the only thing that sends. Two inputs on
 * purpose — 📎 for a PDF already on the phone, 📷 for the certificate lying on the site office desk.
 */
function UploadSlot({ eid, item, onItem }: { eid: string; item: ComplianceItemView; onItem: (i: ComplianceItemView) => void }) {
  const pickRef = useRef<HTMLInputElement>(null);
  const camRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<File[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // review 9/18 #1: lock on the EFFECTIVE status — an expired (or expiring) certificate is still
  // status='confirmed' in the DB but must be renewable from here, or the 待办 card and the daily digest
  // demand an upload the row refuses. The server allows exactly this renewal (compliance_item_renewed).
  const renewable = item.status === 'confirmed' && (item.effective_status === 'expired' || item.effective_status === 'expiring');
  const locked = item.status === 'waived' ? 'waived' : (item.status === 'confirmed' && !renewable) ? 'confirmed' : null;
  const needsExpiry = item.expiry_tracked === 1;

  function clearPick() {
    setPicked([]); setErr(null);
    if (pickRef.current) pickRef.current.value = '';
    if (camRef.current) camRef.current.value = '';
  }

  /** Accumulate across both inputs — 拍照 twice then attach a PDF is a normal sequence on a phone. */
  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const added = [...(e.target.files ?? [])];
    if (!added.length) return;
    const next = [...picked, ...added];
    if (next.length > MAX_FILES) {
      setErr(`一次最多上传 ${MAX_FILES} 个文件，请分批上传。/ At most ${MAX_FILES} files per upload — send them in batches.`);
      e.target.value = '';
      return;
    }
    const tooBig = next.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setErr(`文件「${tooBig.name}」超过 30MB 上限，请压缩或拆分后再上传。/ 「${tooBig.name}」 exceeds the 30MB limit — compress or split it.`);
      e.target.value = '';
      return;
    }
    setErr(null);
    setPicked(next);
    e.target.value = '';   // so picking the SAME file again still fires onChange
  }

  async function confirmUpload() {
    if (!picked.length) return;
    if (needsExpiry && !expiresAt) { setErr('请填写有效期（该证件需要有效期）。/ The validity date is required for this document.'); return; }
    setBusy(true); setErr(null);
    try {
      const { item: updated } = await portalComplianceUpload(eid, item.id, picked, {
        expires_at: expiresAt || undefined, note_md: note.trim() || undefined,
      });
      onItem(updated);
      clearPick(); setExpiresAt(''); setNote('');
      toast(`已上传，等待预算审核。/ Uploaded — WTL will review it.`, { kind: 'success' });
    } catch (e) {
      setErr(apiErrText(e));
    } finally { setBusy(false); }
  }

  if (locked) {
    return (
      <div className="muted small" style={{ marginTop: 6 }}>
        {locked === 'confirmed'
          ? '✓ 已确认，本项已归档，不能再上传或删除。如需更换，请联系预算退回后重新上传。/ Confirmed and filed — ask WTL to return it before replacing anything.'
          : '已免除，本项无需上传。/ Waived — no upload needed.'}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      {renewable && (
        <div className="small" style={{ color: 'var(--warn, #9a6b00)', marginBottom: 4 }}>
          {item.effective_status === 'expired'
            ? '⚠ 已过期 — 请上传新证并填写新的有效期。/ Expired — upload the renewed document with its new validity date.'
            : '⏳ 即将过期 — 可提前上传新证并填写新的有效期。/ Expiring soon — you may file the renewal now with its new validity date.'}
        </div>
      )}
      <div className="row-wrap" style={{ gap: 10, alignItems: 'center' }}>
        <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>📎 选择文件 / Choose files</span>
          <input ref={pickRef} type="file" multiple accept={ACCEPT_ANY} onChange={onPick} disabled={busy} />
        </label>
        <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>📷 拍照上传 / Take a photo</span>
          <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onPick} disabled={busy} />
        </label>
      </div>

      {needsExpiry && (
        <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}>
          <label className="small" htmlFor={`exp-${item.id}`}>有效期至（必填）/ Valid until (required)</label>
          <input id={`exp-${item.id}`} type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} disabled={busy} />
        </div>
      )}

      {picked.length > 0 && (
        <>
          <div className="small muted" style={{ marginTop: 4 }}>
            待上传 / to upload（{picked.length}）：{picked.map((f) => f.name).join('、')}
          </div>
          <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}>
            <input
              className="grow" style={{ minWidth: 140 }} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="备注（选填）/ note (optional)" disabled={busy}
            />
          </div>
          <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}>
            <button className="btn-good" disabled={busy} onClick={confirmUpload}>
              {busy ? '上传中… / uploading…' : '✓ 确认上传 / Confirm upload'}
            </button>
            <button className="small" disabled={busy} onClick={clearPick}>取消 / Cancel</button>
          </div>
        </>
      )}
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{err}</div>}
    </div>
  );
}
